import React, { useMemo, useState } from "react";
import { format, isSameDay } from "date-fns";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, CalendarDays } from "lucide-react";
import { useCustomer } from "@/context/CustomerContext";
import DatasetsTable from "@/components/customer/DatasetsTable";
import PanIndiaFilter from "@/components/customer/PanIndiaFilter";
import ExpiredScreen from "@/components/customer/ExpiredScreen";

export default function DailyUpdates() {
  const { loading, active, datasets, subscription, days_left, renewal_alert, filterOpts, isPanIndia } = useCustomer();
  const [fState, setFState] = useState("");
  const [fCity, setFCity] = useState("");

  const today = useMemo(() => new Date(), []);
  const todayDatasets = (datasets || []).filter((d) => isSameDay(new Date(d.upload_date), today));

  if (loading) return <div className="text-sm text-muted-foreground">Loading…</div>;
  if (!active) return <ExpiredScreen />;

  const total = todayDatasets.reduce((a, b) => a + (b.visible_count || 0), 0);
  const gstCount = todayDatasets.filter((d) => d.type === "gst").length;
  const mcaCount = todayDatasets.filter((d) => d.type === "mca").length;

  return (
    <div className="animate-fade-up">
      {renewal_alert && (
        <div className="mb-6 rounded-lg border border-amber-500/40 bg-amber-500/10 text-amber-900 dark:text-amber-200 px-4 py-3 flex items-start gap-3" data-testid="renewal-banner">
          <AlertTriangle className="h-5 w-5 mt-0.5" />
          <div className="flex-1 text-sm">
            <div className="font-medium">Your subscription expires in {days_left} day{days_left === 1 ? "" : "s"}.</div>
            <div className="opacity-80">Please contact admin to renew and avoid service interruption.</div>
          </div>
        </div>
      )}

      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-md bg-primary/10 text-primary grid place-items-center">
              <CalendarDays className="h-4 w-4" />
            </div>
            <h1 className="font-heading font-bold text-2xl sm:text-3xl">Daily Updates</h1>
            <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/30" variant="outline">Today</Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-1 num">
            {format(today, "EEEE, dd MMM yyyy")} · {todayDatasets.length} file(s) · {total.toLocaleString()} records · {gstCount} GST · {mcaCount} MCA
          </p>
        </div>
      </div>

      <PanIndiaFilter isPanIndia={isPanIndia} filterOpts={filterOpts} fState={fState} setFState={setFState} fCity={fCity} setFCity={setFCity} />

      <DatasetsTable
        datasets={todayDatasets}
        emptyMessage="No files uploaded today yet"
        isPanIndia={isPanIndia}
        fState={fState}
        fCity={fCity}
      />

      {subscription && (
        <Card className="mt-6 p-4 text-xs text-muted-foreground">
          Files older than today are available under <span className="text-primary font-medium">Database</span>. Custom date-range downloads are on <span className="text-primary font-medium">Data Export</span>.
        </Card>
      )}
    </div>
  );
}
